import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit, FaTrash } from "react-icons/fa";
import { getAuthLocalStorage } from "../../../storage/localStorage";
import "../userManagement/style.css";
import "./styles.css";

const API_BASE = process.env.REACT_APP_APIUrl;

function getEffectiveNavOrder(list) {
  const map = {};
  const checked = list.filter((c) => c.isFeatured).sort((a, b) => (a.displayOrder || 9999) - (b.displayOrder || 9999));
  let seq = 0;
  checked.forEach((c) => {
    if (c.displayOrder > 0) {
      map[c._id] = c.displayOrder;
      seq = c.displayOrder;
    } else {
      seq += 1;
      map[c._id] = seq;
    }
  });
  return map;
}

function getEffectiveLandingOrder(list) {
  const map = {};
  const checked = list.filter((c) => c.showOnLandingPage).sort((a, b) => (a.landingPageOrder || 9999) - (b.landingPageOrder || 9999));
  let seq = 0;
  checked.forEach((c) => {
    if (c.landingPageOrder > 0) {
      map[c._id] = c.landingPageOrder;
      seq = c.landingPageOrder;
    } else {
      seq += 1;
      map[c._id] = seq;
    }
  });
  return map;
}

function getNextAvailableNavOrder(list, excludeId) {
  const used = new Set(
    list.filter((c) => c.isFeatured && c._id !== excludeId && c.displayOrder > 0).map((c) => c.displayOrder)
  );
  let k = 1;
  while (used.has(k)) k += 1;
  return k;
}

function getNextAvailableLandingOrder(list, excludeId) {
  const used = new Set(
    list.filter((c) => c.showOnLandingPage && c._id !== excludeId && c.landingPageOrder > 0).map((c) => c.landingPageOrder)
  );
  let k = 1;
  while (used.has(k)) k += 1;
  return k;
}

function slugFromTitle(title) {
  return String(title || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

const FeaturedCategories = () => {
  const navigate = useNavigate();
  const [featuredCategories, setFeaturedCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newPageUrl, setNewPageUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    fetchFeaturedCategories();
  }, []);

  const getAuth = () => {
    const auth = getAuthLocalStorage();
    if (!auth?.loginHeader) {
      alert("Please login again");
      window.location.href = "/admin/login";
      return null;
    }
    return auth.loginHeader;
  };

  const fetchFeaturedCategories = async () => {
    try {
      setLoading(true);
      const token = getAuth();
      if (!token) return;

      const res = await axios.get(`${API_BASE}admin/featuredCategory`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const list = res.data?.data || [];
      const normalized = list.map((cat) => ({
        ...cat,
        displayOrder: typeof cat.displayOrder === "number" ? cat.displayOrder : 0,
        landingPageOrder: typeof cat.landingPageOrder === "number" ? cat.landingPageOrder : 0,
        isFeatured: cat.isFeatured === true,
        showOnLandingPage: cat.showOnLandingPage === true,
        showOnDropshippingPage: cat.showOnDropshippingPage === true, // Added new field
      }));
      normalized.sort((a, b) => a.displayOrder - b.displayOrder);
      setFeaturedCategories(normalized);
    } catch (err) {
      console.error(err);
      if (err.response?.status === 401 || err.response?.status === 402)
        window.location.href = "/admin/login";
      else alert(err.response?.data?.message || "Failed to load categories");
    } finally {
      setLoading(false);
    }
  };

  const update = async (id, payload) => {
    const token = getAuth();
    if (!token) return;
    try {
      const res = await axios.put(
        `${API_BASE}admin/featuredCategory/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      if (res.data?.data) return res.data.data;
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || "Update failed");
    }
    return null;
  };

  const toggleNav = async (id, current, currentOrder) => {
    const list = featuredCategories;
    const navOrders = getEffectiveNavOrder(list);
    const maxNav = Math.max(0, ...Object.values(navOrders));
    const nextOrder = currentOrder === 0 ? maxNav + 1 : currentOrder;
    const payload = current
      ? { isFeatured: false, displayOrder: 0 }
      : { isFeatured: true, displayOrder: nextOrder };
    const updated = await update(id, payload);
    if (updated) {
      const newOrder = current ? 0 : nextOrder;
      setFeaturedCategories((prev) =>
        prev.map((c) => (c._id === id ? { ...c, isFeatured: !current, displayOrder: newOrder } : c))
      );
      setFeaturedCategories((prev) => [...prev].sort((a, b) => a.displayOrder - b.displayOrder));
    }
  };

  const toggleLanding = async (id, current, currentOrder) => {
    const list = featuredCategories;
    const nextOrder = current ? 0 : (currentOrder > 0 ? currentOrder : getNextAvailableLandingOrder(list, id));
    const payload = current
      ? { showOnLandingPage: false, landingPageOrder: 0 }
      : { showOnLandingPage: true, landingPageOrder: nextOrder };
    const updated = await update(id, payload);
    if (updated) {
      const newOrder = current ? 0 : nextOrder;
      setFeaturedCategories((prev) =>
        prev.map((c) => (c._id === id ? { ...c, showOnLandingPage: !current, landingPageOrder: newOrder } : c))
      );
    }
  };

  // Added new toggle function for dropshipping page
  const toggleDropshipping = async (id, current) => {
    const payload = { showOnDropshippingPage: !current };
    const updated = await update(id, payload);
    if (updated) {
      setFeaturedCategories((prev) =>
        prev.map((c) => (c._id === id ? { ...c, showOnDropshippingPage: !current } : c))
      );
    }
  };

  const saveNavPriority = async (id, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    const updated = await update(id, { displayOrder: num });
    if (updated) {
      setFeaturedCategories((prev) =>
        prev.map((c) => (c._id === id ? { ...c, displayOrder: num } : c))
      );
      setFeaturedCategories((prev) => [...prev].sort((a, b) => a.displayOrder - b.displayOrder));
    }
  };

  const saveLandingPriority = async (id, value) => {
    const num = parseInt(value, 10);
    if (isNaN(num) || num < 0) return;
    const updated = await update(id, { landingPageOrder: num });
    if (updated) {
      setFeaturedCategories((prev) =>
        prev.map((c) => (c._id === id ? { ...c, landingPageOrder: num } : c))
      );
    }
  };

  const createCategory = async () => {
    const tagName = newTitle.trim();
    const pageUrl = (newPageUrl.trim() || slugFromTitle(newTitle)).replace(/^\//, "") || slugFromTitle(newTitle);
    if (!tagName) {
      alert("Title is required.");
      return;
    }
    if (!pageUrl) {
      alert("Page URL is required (e.g. new-arrival).");
      return;
    }
    const token = getAuth();
    if (!token) return;
    setCreating(true);
    try {
      const res = await axios.post(
        `${API_BASE}admin/featuredCategory`,
        { tagName, pageUrl },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      if (res.data?.data?._id) {
        setCreateModal(false);
        setNewTitle("");
        setNewPageUrl("");
        fetchFeaturedCategories();
        // window.location.href = `/admin/featured-categories/edit?id=${res.data.data._id}`;
        navigate(`/admin/featured-categories/edit?id=${res.data.data._id}`);
      } else {
        alert(res.data?.message || "Create failed.");
      }
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Create failed.");
    } finally {
      setCreating(false);
    }
  };

  const deleteCategory = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    const token = getAuth();
    if (!token) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API_BASE}admin/featuredCategory/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFeaturedCategories((prev) => prev.filter((c) => c._id !== id));
    } catch (err) {
      alert(err.response?.data?.message || err.message || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  const saveAll = async () => {
    if (featuredCategories.length === 0) return;
    setSaving(true);
    try {
      let ok = 0;
      for (const row of featuredCategories) {
        const payload = {
          displayOrder: row.displayOrder ?? 0,
          landingPageOrder: row.landingPageOrder ?? 0,
          isFeatured: row.isFeatured === true,
          showOnLandingPage: row.showOnLandingPage === true,
          showOnDropshippingPage: row.showOnDropshippingPage === true, // Added to saveAll payload
        };
        const updated = await update(row._id, payload);
        if (updated) ok += 1;
      }
      if (ok === featuredCategories.length) {
        alert("Saved successfully.");
      } else if (ok > 0) {
        alert(`Saved ${ok} of ${featuredCategories.length}. Check failed rows.`);
      }
    } catch (err) {
      alert(err?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="um-page fc-list-page">
      <header className="fc-list-header">
        <div className="fc-list-actions">
          <button
            type="button"
            className="fc-btn fc-btn--new"
            onClick={() => {
              setNewTitle("");
              setNewPageUrl("");
              setCreateModal(true);
            }}
          >
            New
          </button>
          <button
            type="button"
            className="fc-btn fc-btn--primary fc-btn--save"
            onClick={saveAll}
            disabled={saving || featuredCategories.length === 0}
            title="Save all menu and homepage settings"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </header>

      {createModal && (
        <div className="fc-modal-overlay" onClick={() => !creating && setCreateModal(false)}>
          <div className="fc-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="fc-modal-title">New featured category</h3>
            <div className="fc-modal-field">
              <label>Title</label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => {
                  setNewTitle(e.target.value);
                  if (!newPageUrl || newPageUrl === slugFromTitle(newTitle)) setNewPageUrl(slugFromTitle(e.target.value));
                }}
                placeholder="e.g. New Arrival"
                className="fc-modal-input"
              />
            </div>
            <div className="fc-modal-field">
              <label>Page URL</label>
              <input
                type="text"
                value={newPageUrl}
                onChange={(e) => setNewPageUrl(e.target.value)}
                placeholder="e.g. new-arrival"
                className="fc-modal-input"
              />
            </div>
            <div className="fc-modal-actions">
              <button type="button" className="fc-btn fc-btn--secondary" onClick={() => !creating && setCreateModal(false)} disabled={creating}>
                Cancel
              </button>
              <button type="button" className="fc-btn fc-btn--primary" onClick={createCategory} disabled={creating}>
                {creating ? "Creating…" : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div className="fc-list-loading">
          <span className="fc-list-loading-text">Loading categories…</span>
        </div>
      )}

      {!loading && featuredCategories.length === 0 && (
        <div className="fc-list-empty">
          <p>No featured categories yet.</p>
        </div>
      )}

      {!loading && featuredCategories.length > 0 && (
        <div className="fc-list-card">
          <div className="fc-table-wrap">
            <table className="fc-table">
              <thead>
                <tr>
                  <th className="fc-th fc-th--name">Category</th>
                  <th className="fc-th fc-th--status">Status</th>
                  <th className="fc-th fc-th--products">Total products</th>
                  <th className="fc-th fc-th--nav">Show in menu</th>
                  <th className="fc-th fc-th--order">Menu order</th>
                  <th className="fc-th fc-th--landing">Show on homepage</th>
                  <th className="fc-th fc-th--order">Homepage order</th>
                  <th className="fc-th fc-th--dropshipping">Show on dropshipping</th> {/* New Header */}
                  <th className="fc-th fc-th--action">Action</th>
                </tr>
              </thead>
              <tbody>
                {featuredCategories.map((row) => (
                  <tr key={row._id} className="fc-tr">
                    <td className="fc-td fc-td--name">
                      <span className="fc-category-name">{row.tagName}</span>
                    </td>
                    <td className="fc-td fc-td--status">
                      <span className={`fc-badge fc-badge--${row.status || "active"}`}>
                        {(row.status || "active").charAt(0).toUpperCase() + (row.status || "active").slice(1)}
                      </span>
                    </td>
                    <td className="fc-td fc-td--products" title="Total products tagged with this category">
                      {row.totalProductCount ?? "—"}
                    </td>
                    <td className="fc-td fc-td--nav">
                      <label className="fc-check" title="Show this category in the top navigation">
                        <input
                          type="checkbox"
                          checked={row.isFeatured}
                          onChange={() => toggleNav(row._id, row.isFeatured, row.displayOrder)}
                        />
                        <span className="fc-check-box" />
                      </label>
                    </td>
                    <td className="fc-td fc-td--order">
                      <input
                        type="number"
                        min={0}
                        className="fc-order-input"
                        value={row.isFeatured && row.displayOrder === 0 ? (getEffectiveNavOrder(featuredCategories)[row._id] ?? 1) : row.displayOrder}
                        onChange={(e) => {
                          const v = e.target.value;
                          const n = v === "" ? 0 : parseInt(v, 10);
                          if (v !== "" && isNaN(n)) return;
                          setFeaturedCategories((prev) =>
                            prev.map((c) =>
                              c._id === row._id ? { ...c, displayOrder: v === "" ? 0 : n } : c
                            )
                          );
                        }}
                        onBlur={(e) => saveNavPriority(row._id, e.target.value)}
                        title="Lower number = appears first in menu"
                      />
                    </td>
                    <td className="fc-td fc-td--landing">
                      <label className="fc-check" title="Show this category on the homepage">
                        <input
                          type="checkbox"
                          checked={row.showOnLandingPage}
                          onChange={() => toggleLanding(row._id, row.showOnLandingPage, row.landingPageOrder)}
                        />
                        <span className="fc-check-box" />
                      </label>
                    </td>
                    <td className="fc-td fc-td--order">
                      <input
                        type="number"
                        min={0}
                        className="fc-order-input"
                        value={row.showOnLandingPage && row.landingPageOrder === 0 ? (getEffectiveLandingOrder(featuredCategories)[row._id] ?? 1) : row.landingPageOrder}
                        onChange={(e) => {
                          const v = e.target.value;
                          const n = v === "" ? 0 : parseInt(v, 10);
                          if (v !== "" && isNaN(n)) return;
                          setFeaturedCategories((prev) =>
                            prev.map((c) =>
                              c._id === row._id ? { ...c, landingPageOrder: v === "" ? 0 : n } : c
                            )
                          );
                        }}
                        onBlur={(e) => saveLandingPriority(row._id, e.target.value)}
                        title="Lower number = appears first on homepage"
                      />
                    </td>
                    
                    {/* New Cell for Dropshipping Checkbox */}
                    <td className="fc-td fc-td--dropshipping">
                      <label className="fc-check" title="Show this category on the dropshipping page">
                        <input
                          type="checkbox"
                          checked={row.showOnDropshippingPage || false}
                          onChange={() => toggleDropshipping(row._id, row.showOnDropshippingPage)}
                        />
                        <span className="fc-check-box" />
                      </label>
                    </td>

                    <td className="fc-td fc-td--action">
                      <div className="fc-action-btns">
                        <button
                          type="button"
                          className="fc-btn-icon fc-btn-icon--edit"
                          onClick={() => navigate(`/admin/featured-categories/edit?id=${row._id}`)}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          className="fc-btn-icon fc-btn-icon--danger"
                          onClick={() => deleteCategory(row._id, row.tagName)}
                          disabled={deletingId === row._id}
                          title="Delete"
                          aria-label="Delete"
                        >
                          {deletingId === row._id ? <span className="fc-icon-dots">…</span> : <FaTrash />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedCategories;